import type { Document } from "../document/Document";
import type { Command } from "./Command";

interface Entry {
  readonly command: Command;
  readonly inverse: Command;
}

/**
 * A named snapshot captured at a particular history state.
 * Snapshots are preserved independently of undo/redo — they never get
 * popped from the stack when the user makes new commands.
 */
export interface Snapshot {
  readonly id: string;
  readonly name: string;
  /** The document state at the moment the snapshot was taken. */
  readonly doc: Document;
  /** The past-stack length at capture time (informational). */
  readonly historyIndex: number;
}

export class History {
  private past: Entry[] = [];
  private future: Entry[] = [];
  private maxStates: number;

  /** Named snapshots — independent of the undo/redo stack. */
  private _snapshots: Snapshot[] = [];
  private _snapshotCounter = 0;

  /**
   * @param maxStates Initial history-states cap (Photoshop's
   *   Preferences > Performance > History States). Defaults to 50 to match
   *   the previous hard-coded behavior. Callers that need every document to
   *   honor the user's preference should pass it here rather than calling
   *   setMaxStates() only on the currently-active History (#148).
   */
  constructor(maxStates = 50) {
    this.maxStates = Math.max(1, maxStates);
  }

  execute(doc: Document, command: Command): Document {
    const next = command.apply(doc);
    const inverse = command.invert(doc, next);
    if (!command.transient) {
      // Command coalescing: if the new command implements coalesceWith and
      // it returns true against the most-recent past command, merge them by
      // replacing the last entry's forward command with the new one while
      // keeping the original inverse (so undo goes all the way back).
      const last = this.past[this.past.length - 1];
      if (
        last &&
        typeof (command as unknown as { coalesceWith?: unknown }).coalesceWith === "function" &&
        (command as unknown as { coalesceWith: (other: Command) => boolean }).coalesceWith(last.command)
      ) {
        // Replace the last entry: keep its original inverse but advance the
        // forward command to the latest one.
        this.past[this.past.length - 1] = { command, inverse: last.inverse };
      } else {
        this.past.push({ command, inverse });
        while (this.past.length > this.maxStates) {
          this.past.shift();
        }
      }
    }
    // Only clear redo for non-transient commands (PS-correct: transient
    // selection previews must not destroy the redo stack).
    if (!command.transient) {
      this.future = [];
    }
    return next;
  }

  undo(doc: Document): Document {
    const entry = this.past.pop();
    if (!entry) return doc;
    const next = entry.inverse.apply(doc);
    this.future.push(entry);
    return next;
  }

  redo(doc: Document): Document {
    const entry = this.future.pop();
    if (!entry) return doc;
    const next = entry.command.apply(doc);
    this.past.push(entry);
    return next;
  }

  get canUndo(): boolean {
    return this.past.length > 0;
  }
  get canRedo(): boolean {
    return this.future.length > 0;
  }

  /** Return a copy of the past entries. */
  getPastEntries(): ReadonlyArray<Entry> {
    return [...this.past];
  }

  /** Return a copy of the future entries. */
  getFutureEntries(): ReadonlyArray<Entry> {
    return [...this.future];
  }

  /** Set the maximum number of history states to retain. */
  setMaxStates(n: number): void {
    this.maxStates = Math.max(1, n);
    // Trim if current history exceeds new limit
    while (this.past.length > this.maxStates) {
      this.past.shift();
    }
  }

  /**
   * Step backward by N states (Photoshop: History panel click, or Alt+Ctrl+Z).
   * Unlike undo, this can step back multiple states at once.
   */
  stepBackward(doc: Document, steps = 1): Document {
    let result = doc;
    for (let i = 0; i < steps; i++) {
      const entry = this.past.pop();
      if (!entry) break;
      result = entry.inverse.apply(result);
      this.future.push(entry);
    }
    return result;
  }

  /**
   * Step forward by N states (Photoshop: Shift+Ctrl+Z).
   * Unlike redo, this can step forward multiple states at once.
   */
  stepForward(doc: Document, steps = 1): Document {
    let result = doc;
    for (let i = 0; i < steps; i++) {
      const entry = this.future.pop();
      if (!entry) break;
      result = entry.command.apply(result);
      this.past.push(entry);
    }
    return result;
  }

  /** Clear all history entries (does not clear snapshots). */
  clear(): void {
    this.past = [];
    this.future = [];
  }

  // ── Snapshots ──────────────────────────────────────────────────────────────

  /**
   * Capture the current document state as a named snapshot.
   * Snapshots survive undo/redo and new commands — they are never auto-popped.
   * The caller passes the current live document so the snapshot is self-contained.
   */
  takeSnapshot(doc: Document, name?: string): Snapshot {
    const id = `snap-${++this._snapshotCounter}`;
    const label = name ?? `Snapshot ${this._snapshotCounter}`;
    const snap: Snapshot = {
      id,
      name: label,
      doc,
      historyIndex: this.past.length,
    };
    this._snapshots.push(snap);
    return snap;
  }

  /**
   * Return the document stored in the snapshot.
   * The caller should replace the live document with the returned value to
   * implement "jump to snapshot" (and optionally clear / reset history).
   */
  jumpToSnapshot(snap: Snapshot): Document {
    return snap.doc;
  }

  /**
   * Delete a snapshot by id. Returns true if found and removed.
   */
  deleteSnapshot(id: string): boolean {
    const idx = this._snapshots.findIndex((s) => s.id === id);
    if (idx < 0) return false;
    this._snapshots.splice(idx, 1);
    return true;
  }

  /** Return a copy of the snapshots list. */
  getSnapshots(): ReadonlyArray<Snapshot> {
    return [...this._snapshots];
  }

  /** Clear all snapshots. */
  clearSnapshots(): void {
    this._snapshots = [];
  }

  /**
   * Reconstruct the document at a given state index.
   * index 0 = initial document (before any commands)
   * index N = document after applying the first N commands from the past stack
   */
  getDocumentAt(doc: Document, index: number): Document {
    const total = this.past.length;
    if (index < 0 || index > total) return doc;
    if (index === total) return doc;

    // Undo forward to reach the target state
    const undoCount = total - index;
    let result = doc;
    const undone: Entry[] = [];
    for (let i = 0; i < undoCount; i++) {
      const entry = this.past.pop();
      if (!entry) break;
      result = entry.inverse.apply(result);
      undone.push(entry);
    }
    // Restore the undone entries (in reverse order)
    while (undone.length > 0) {
      const entry = undone.pop()!;
      this.past.push(entry);
    }
    return result;
  }
}
